/**
 * usePackageTransmission Hook
 *
 * RTKQ-based transmission state management with optimistic updates.
 * Uses selectFromResult for granular subscriptions - components only
 * re-render when transmission data actually changes.
 *
 * Contacts and email defaults come embedded in the package response,
 * eliminating the need for separate API calls.
 */
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useMatter } from '@/hooks/useMatter';
import { mattersApiClient } from '@/services/api';
import {
  correspondenceApi,
  type CorrespondenceChannel,
  type CustomUserSchema,
  type DestinationAddress,
  type Transmission,
  useCorrespondencePackageRetrieveQuery,
  useCorrespondencePackageTransmissionUpdate2Mutation,
} from '@/services/api/correspondenceService';
import type { RecipientContactHandle } from '@/services/api/generated/recipientDirectoryService';

import {
  createEmptyTransmission,
  type TransmissionOption,
} from '../transmission/types';
import { isValidTransmission } from '../transmission/validation';
import type { ContactChannel, RecipientContact } from '../types';

/** Channels that have contact handles (excludes mailed_cheque which is a special case) */
const CONTACT_CHANNELS: ContactChannel[] = ['fax', 'email', 'mailed_letter'];

/** Check if a channel type supports contact selection */
function isContactChannel(
  channel: CorrespondenceChannel,
): channel is ContactChannel {
  return CONTACT_CHANNELS.includes(channel as ContactChannel);
}

/** Convert API RecipientContactHandle to frontend RecipientContact */
function toRecipientContact(h: RecipientContactHandle): RecipientContact {
  return {
    id: h.id,
    handleId: h.handle_id,
    displayName: h.display_name,
    label: h.label ?? undefined,
    faxNumber: h.fax_number ?? undefined,
    email: h.email ?? undefined,
    address: h.address ?? undefined,
    supportedChannels: h.supported_channels as ContactChannel[],
    handleType: h.handle_type,
    source: {
      entityType: h.source.entity_type,
      entityId: h.source.entity_id,
      entityName: h.source.entity_name,
    },
  };
}

// ============================================================================
// Types
// ============================================================================

export interface UsePackageTransmissionOptions {
  packageId: string | undefined;
  /** Skip fetching (e.g., while package is being created) */
  skip?: boolean;
}

export interface UsePackageTransmissionResult {
  // Transmission form state
  transmission: Partial<Transmission> | null;
  channel: CorrespondenceChannel | null;
  isValid: boolean;
  destinationDisplay: string | null;

  // Package transmission status
  isTransmitted: boolean;
  transmittedAt: string | null;
  transmittedBy: CustomUserSchema | null;

  // PostGrid references (for viewing transmitted mail/cheques)
  postgridLetterId: string | null;
  postgridChequeId: string | null;

  // Entity contacts (embedded in package response)
  entityContacts: RecipientContact[];
  entityName: string | null;

  // Recipient info (for add contact when no contacts exist)
  recipientId: string | null;
  recipientEntityType: string | null;

  // Unified selection
  selectedOption: TransmissionOption | null;
  selectTransmissionOption: (option: TransmissionOption) => void;

  // Actions
  updateTransmission: (partial: Partial<Transmission>) => void;
  buildTransmission: () => Transmission | null;

  // Matter CMS email (for auto-CC feature)
  matterCmsEmail: string | null;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

function getDestinationDisplay(
  transmission: Partial<Transmission> | null,
): string | null {
  if (!transmission?.type) return null;

  switch (transmission.type) {
    case 'fax':
      return transmission.fax_number || null;
    case 'email':
      return transmission.email || null;
    case 'mailed_letter':
    case 'mailed_cheque':
      if (transmission.address) {
        return [
          transmission.address.address_line1,
          transmission.address.city,
          transmission.address.state,
          transmission.address.zip_code,
        ]
          .filter(Boolean)
          .join(', ');
      }
      return null;
    default:
      return null;
  }
}

function populateFromContact(
  contact: RecipientContact,
  channel: CorrespondenceChannel,
): Partial<Transmission> {
  const base = {
    display_name: contact.displayName,
    entity_type: contact.source.entityType as Transmission['entity_type'],
    entity_id: contact.source.entityId,
    source: contact.handleId
      ? {
          source_type: 'contact_handle' as const,
          handle_id: contact.handleId,
          handle_type: contact.handleType,
        }
      : undefined,
  };

  switch (channel) {
    case 'fax':
      return { ...base, type: 'fax', fax_number: contact.faxNumber };
    case 'email':
      return { ...base, type: 'email', email: contact.email };
    case 'mailed_letter':
      return {
        ...base,
        type: 'mailed_letter',
        address: contact.address as DestinationAddress,
        mailing_class: 'first_class',
      };
    default:
      return base;
  }
}

// ============================================================================
// Hook
// ============================================================================

export function usePackageTransmission({
  packageId,
  skip = false,
}: UsePackageTransmissionOptions): UsePackageTransmissionResult {
  const { matterId } = useMatter();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  // ========== RTKQ Query with selectFromResult ==========
  // Subscribes to transmission-related fields and embedded contacts/defaults
  const {
    transmission,
    isTransmitted,
    transmittedAt,
    transmittedBy,
    entityContacts,
    entityName,
    recipientId,
    recipientEntityType,
    emailDefaults,
    backendDestinationDisplay,
    postgridLetterId,
    postgridChequeId,
    isLoading,
  } = useCorrespondencePackageRetrieveQuery(
    { matterId, id: packageId || '' },
    {
      skip: skip || !packageId,
      selectFromResult: ({ data, isLoading: loading }) => {
        // Contacts are now embedded in the response
        const rawContacts = data?.recipient_contacts ?? [];
        const contacts = rawContacts.map(toRecipientContact);

        // Get entity name from first contact's source, or fall back to display_name
        const name =
          contacts[0]?.source.entityName ??
          data?.recipient_display_name ??
          null;

        return {
          transmission: data?.transmission ?? null,
          isTransmitted: !!data?.transmitted_at,
          transmittedAt: data?.transmitted_at ?? null,
          transmittedBy: data?.transmitted_by ?? null,
          entityContacts: contacts,
          entityName: name,
          recipientId: (data?.recipient_id as string) ?? null,
          recipientEntityType: data?.recipient_entity_type ?? null,
          emailDefaults: data?.email_defaults ?? null,
          backendDestinationDisplay:
            data?.transmission_destination_display ?? null,
          postgridLetterId:
            (data?.message?.postgrid_letter_id as string) ?? null,
          postgridChequeId:
            (data?.message?.postgrid_cheque_id as string) ?? null,
          isLoading: loading,
        };
      },
    },
  );

  // ========== Fetch matter CMS email ==========
  const { data: matterData } = mattersApiClient.useGetMatterQuery(matterId, {
    skip: !matterId,
  });
  const matterCmsEmail = matterData?.cms_email ?? null;

  // ========== Mutation with Optimistic Updates ==========
  const [updateTransmissionApi, { isLoading: isSaving }] =
    useCorrespondencePackageTransmissionUpdate2Mutation();

  // ========== Computed Values ==========
  const channel = (transmission?.type as CorrespondenceChannel) || null;

  const isValid = useMemo(
    () => isValidTransmission(transmission),
    [transmission],
  );

  // Prefer backend-computed destination display, fall back to local computation
  const destinationDisplay = useMemo(
    () => backendDestinationDisplay ?? getDestinationDisplay(transmission),
    [backendDestinationDisplay, transmission],
  );

  // Index contacts by handleId for O(1) lookup (handleId is unique, handleType is not)
  const contactsByHandleId = useMemo(
    () => new Map(entityContacts.map((c) => [c.handleId, c])),
    [entityContacts],
  );

  // Derive selectedOption from transmission data (unified selection pattern)
  const selectedOption: TransmissionOption | null = useMemo(() => {
    if (!channel) return null;

    // Check if using a contact handle - O(1) lookup via Map using unique handleId
    const handleId = transmission?.source?.handle_id;
    if (transmission?.source?.source_type === 'contact_handle' && handleId) {
      const contact = contactsByHandleId.get(handleId);
      if (contact && isContactChannel(channel)) {
        return { type: 'contact', contact, channel };
      }
    }

    // Otherwise it's manual entry
    return { type: 'manual', channel };
  }, [channel, transmission?.source, contactsByHandleId]);

  // ========== Save to Backend ==========
  const saveTransmission = useCallback(
    async (newTransmission: Partial<Transmission>) => {
      if (!packageId) return;

      // Optimistic update - instantly update RTKQ cache
      const patchResult = dispatch(
        correspondenceApi.util.updateQueryData(
          'correspondencePackageRetrieve',
          { matterId, id: packageId },
          (draft) => {
            draft.transmission = newTransmission as Transmission;
          },
        ),
      );

      try {
        await updateTransmissionApi({
          id: packageId,
          matterId,
          patchedTransmission: newTransmission as Transmission,
        }).unwrap();
      } catch {
        patchResult.undo();
      }
    },
    [dispatch, matterId, packageId, updateTransmissionApi],
  );

  // ========== Actions ==========
  const updateTransmission = useCallback(
    (partial: Partial<Transmission>) => {
      saveTransmission({
        ...transmission,
        ...partial,
      });
    },
    [transmission, saveTransmission],
  );

  const buildTransmission = useCallback((): Transmission | null => {
    if (!isValidTransmission(transmission)) return null;
    return transmission as Transmission;
  }, [transmission]);

  // Unified selection action
  const selectTransmissionOption = useCallback(
    (option: TransmissionOption) => {
      // Base transmission data from contact or empty manual entry
      const baseTransmission =
        option.type === 'contact'
          ? populateFromContact(option.contact, option.channel)
          : {
              ...createEmptyTransmission(option.channel),
              display_name: entityName || '',
              source: undefined,
            };

      // Add email defaults if selecting email channel
      const withDefaults =
        option.channel === 'email' && emailDefaults
          ? {
              ...baseTransmission,
              subject: emailDefaults.subject,
              body: emailDefaults.body,
            }
          : baseTransmission;

      saveTransmission(withDefaults);
    },
    [saveTransmission, entityName, emailDefaults],
  );

  return {
    // Transmission form state
    transmission,
    channel,
    isValid,
    destinationDisplay,

    // Package transmission status
    isTransmitted,
    transmittedAt,
    transmittedBy,

    // PostGrid references (for viewing transmitted mail/cheques)
    postgridLetterId,
    postgridChequeId,

    // Entity contacts (embedded in package response)
    entityContacts,
    entityName,

    // Recipient info (for add contact when no contacts exist)
    recipientId,
    recipientEntityType,

    // Unified selection
    selectedOption,
    selectTransmissionOption,

    // Actions
    updateTransmission,
    buildTransmission,

    // Matter CMS email (for auto-CC feature)
    matterCmsEmail,

    // Loading
    isLoading,
    isSaving,
  };
}
